/**
 * §v3.route-prefetch (2026-05-07) — Linear-pattern hover prefetch
 * for sidebar navigation. Hover a sidebar item → fire the same
 * query that the destination page would fire on mount, with the
 * SAME queryKey react-query expects. By the time the operator
 * clicks, the destination page reads from the warm cache and
 * renders instantly.
 *
 * Each entry below mirrors the page's default queryKey + queryFn.
 * If a page changes its default filter shape, the prefetch silently
 * misses (creates an unused cache entry) — not catastrophic but
 * worth keeping in sync.
 */
import type { QueryClient } from "@tanstack/react-query";
import {
  getAnalyticsSummary,
  listCustomers,
  listOrders,
  listSupportTickets,
} from "./api";
import { listConversations } from "./conversations";
import { listLiveCalls } from "./live";
import { listProducts } from "./products";

const STALE = 30_000;

/** Run a prefetch for the given top-level route path. No-op for
 *  paths without a known main query (test-chat, test-voice,
 *  settings, etc.) — those are interactive pages. */
export function prefetchRoute(client: QueryClient, path: string): void {
  // Normalise — strip query string + trailing slash.
  const p = path.replace(/\?.*$/, "").replace(/\/$/, "") || "/";

  const fire = (key: readonly unknown[], fn: () => Promise<unknown>) =>
    client.prefetchQuery({
      queryKey: key as unknown[],
      queryFn: fn,
      staleTime: STALE,
    });

  switch (p) {
    case "/overview": {
      // Three parallel feeds — Overview's KPI cards + lists.
      fire(["conversations", { category: "customer", limit: 100 }], () =>
        listConversations({ category: "customer" as const, limit: 100 }),
      );
      fire(["orders", { limit: 100 }], () => listOrders({}));
      fire(["support-tickets", { limit: 100 }], () =>
        listSupportTickets({}),
      );
      return;
    }

    case "/inbox": {
      // Inbox default filters (see InboxPage useState defaults).
      const filters = {
        category: "customer" as const,
        channel: "",
        status: "",
        sentiment: "",
        q: "",
        limit: 100,
      };
      fire(["conversations", filters], () => listConversations(filters));
      return;
    }

    case "/live-calls": {
      fire(["live-calls"], () => listLiveCalls());
      return;
    }

    case "/orders": {
      const filters = { status: "", channel: "", query: "" };
      fire(["orders", filters], () => listOrders(filters));
      return;
    }

    case "/support": {
      const filters = {
        status: "",
        priority: "",
        channel: "",
        issue_type: "",
        query: "",
      };
      fire(["support-tickets", filters], () =>
        listSupportTickets(filters),
      );
      // Customer-name enrichment query the support list also fires.
      fire(["customers-list", "support-enrich"], () =>
        listCustomers("", 500),
      );
      return;
    }

    case "/customers": {
      // CustomerProfilePage uses a debounced search/filters key. The
      // initial render fires with empty query + EMPTY_BROWSE_FILTERS.
      const browseFilters = {
        gender: "all",
        city: "",
        minSpend: "",
        maxSpend: "",
        lastPurchaseFrom: "",
        lastPurchaseTo: "",
      };
      fire(["customers-list", "", browseFilters], () =>
        listCustomers({ q: "" }),
      );
      // Plus the all-cities catalog the dropdown depends on.
      fire(["customers-list", "all-cities"], () => listCustomers({ q: "" }));
      return;
    }

    case "/products": {
      fire(["products"], () => listProducts());
      return;
    }

    case "/agent-studio": {
      // §v3.train-retired (2026-06-01) — the Train tab (and its training-stats
      // prewarm) was removed; the default tab is Persona, which loads its own
      // voice-picks + brand-facts on mount.
      return;
    }

    case "/analytics": {
      // Analytics defaults: 30 days, includeTests = false.
      fire(["analytics", "summary", 30, false], () =>
        getAnalyticsSummary(30, { includeTests: false }),
      );
      return;
    }

    default:
      // /test-chat, /test-voice, /settings — interactive surfaces with
      // no main fetch. No-op.
      return;
  }
}

/**
 * §v3.chunk-prefetch (2026-05-11) — fire the lazy-loaded JS chunk
 * for a sidebar destination on hover, so by the time the user
 * clicks the chunk is already in browser cache and the click→paint
 * latency drops to near zero. Without this, the user clicks a
 * sidebar item, React starts fetching the chunk, and the Suspense
 * fallback (now a layout-preserving skeleton inside AppShell, but
 * still a visible state change) is shown for 200-800 ms on slower
 * networks. With this, the chunk is usually already cached by the
 * time the click fires.
 *
 * Cached dynamic imports — calling `import("./Foo")` a second time
 * returns the already-resolved module from the module-graph cache,
 * so a hover that fires the import is cheap on repeated hovers.
 *
 * Each entry mirrors the `lazyPage()` call in App.tsx — if a route
 * gets added there it should be added here too. Missing routes
 * fall through to a no-op (data prefetch still runs above).
 */
const CHUNK_LOADERS: Record<string, () => Promise<unknown>> = {
  "/overview": () => import("@/pages/Overview"),
  "/inbox": () => import("@/pages/ConversationDetail"),
  "/live-calls": () => import("@/pages/LiveCalls"),
  "/enquiries": () => import("@/pages/Enquiries"),
  "/orders": () => import("@/pages/Orders"),
  "/support": () => import("@/pages/SupportTickets"),
  "/customers": () => import("@/pages/CustomerProfile"),
  "/products": () => import("@/pages/Products"),
  "/products/qa": () => import("@/pages/Products"),
  "/agent-studio": () => import("@/pages/agent-studio/AgentStudio"),
  "/test-chat": () => import("@/pages/TestChat"),
  "/test-voice": () => import("@/pages/TestVoice"),
  // §v3.analytics-retired (2026-05-12) — Analytics surface removed;
  // Reports covers the same ground. The /analytics route in App.tsx
  // 301s to /reports, so the prefetch row is no longer needed.
  "/reports": () => import("@/pages/Reports"),
  "/marketing": () => import("@/pages/Marketing"),
  // §settings-redesign — the left settings sidebar warms each focused page on
  // hover so switching paints without a Suspense flash. (Security + What's new
  // are eager-imported in App.tsx, so they have no separate chunk to prefetch.)
  "/settings": () => import("@/pages/settings/sections"),
  "/settings/channels": () => import("@/pages/settings/sections"),
  "/settings/phone": () => import("@/pages/settings/sections"),
  "/settings/notifications": () => import("@/pages/settings/sections"),
  "/settings/marketing": () => import("@/pages/settings/sections"),
  "/settings/payments": () => import("@/pages/settings/sections"),
  "/settings/members": () => import("@/pages/Users"),
  "/settings/roles": () => import("@/pages/RolesPermissions"),
};

const _PREFETCHED_CHUNKS = new Set<string>();

/** Trigger lazy chunk download for a route. No-op for unknown routes
 *  or routes whose chunk has already been requested. */
export function prefetchRouteChunk(path: string): void {
  const p = path.replace(/\?.*$/, "").replace(/\/$/, "") || "/";
  if (_PREFETCHED_CHUNKS.has(p)) return;
  const loader = CHUNK_LOADERS[p];
  if (!loader) return;
  _PREFETCHED_CHUNKS.add(p);
  // Fire and forget. Errors are silent — if the chunk fails to
  // load on hover, the regular Suspense path will surface it on
  // click instead.
  loader().catch(() => {
    // Re-allow retry on a future hover/click.
    _PREFETCHED_CHUNKS.delete(p);
  });
}
